import { useEffect } from 'react';

/**
 * When a deployed build is allowed to take effect.
 *
 * `registerType: 'prompt'` (vite.config.ts) means the app decides *when* to
 * swap builds, and it used to decide by asking: a banner with "Opnieuw laden"
 * on it. Asking turned out to be the wrong half of the feature. The person who
 * taps the button is the one who already knew a deploy happened; the phone that
 * keeps the old build is the one that ends up quietly disagreeing with the
 * other phone, and neither person is told that is what is going on.
 *
 * Het Woord is where that surfaces worst. The word lists are bundled into the
 * client and `getDailyTarget` hashes the date into them (WORDLE.md), so two
 * phones on two builds compute two *different* words for the same day — the
 * daily stops being shared, the duel compares two puzzles, and the only visible
 * symptom is that one of you is wrong about what today's word was.
 *
 * So the app applies an update by itself now. The prompt's real reason for
 * existing was never "ask permission" — it was "don't throw away the screen
 * someone is standing in front of", which is a question about the *moment*, not
 * about consent. This module answers that question and nothing else; the
 * service-worker wiring is in `UpdatePrompt.tsx`.
 */

/**
 * Screens that have asked not to be reloaded, as a count rather than a flag:
 * two of them can be mounted at once during a route transition, and a flag
 * would be cleared by whichever unmounted first.
 */
let holds = 0;
const listeners = new Set<() => void>();

/**
 * Hold updates off while this screen is on.
 *
 * For the rare screen whose state lives only in React and costs real effort to
 * get back. Two use it: cook mode, which is held open for an hour and would
 * drop somebody back on step 1 with a pan on the heat; and an imported recipe
 * draft, which cost a photo and a model call and is not in any database until
 * it is saved. Everything else in this app reads from Dexie and the URL, so a
 * reload costs a scroll position at worst.
 *
 * The hold is a delay, never a veto — the update is applied the moment the
 * screen lets go.
 */
export function useHoldUpdates(active = true): void {
  useEffect(() => {
    if (!active) return;
    holds += 1;
    return () => {
      holds -= 1;
      // Releasing is the moment worth reconsidering: cooking just finished.
      for (const notify of [...listeners]) notify();
    };
  }, [active]);
}

/** Run `fn` when a hold is released. Returns an unsubscribe. */
export function onHoldsReleased(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** Whatever is being typed right now is the one thing on screen that is in no database yet. */
function hasUnsavedTyping(el: Element | null): boolean {
  if (!el) return false;
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) {
    // Focused but empty is nothing to lose — the login screen sitting there
    // with the cursor in an untouched e-mail field must not hold a deploy off.
    return el.value.trim() !== '';
  }
  return el instanceof HTMLElement && el.isContentEditable && (el.textContent ?? '').trim() !== '';
}

/**
 * Whether reloading right now would cost anything.
 *
 * Deliberately cheap and structural — it reads the DOM rather than asking every
 * screen to declare itself, because a rule each screen has to remember is a
 * rule that is wrong in the screen somebody adds next month.
 */
export function safeToReload(): boolean {
  // A hold outranks everything below, including the screen being off: cook mode
  // holds a wake lock, and a phone that goes dark in a kitchen is a phone on a
  // counter, not an app nobody is using.
  if (holds > 0) return false;
  if (typeof document === 'undefined') return true;
  // Nobody is looking. The best possible moment, and the common one — a PWA
  // spends most of its life backgrounded.
  if (document.visibilityState === 'hidden') return true;
  if (hasUnsavedTyping(document.activeElement)) return false;
  // An open sheet is a decision in progress: quantity, a caregiver, a category.
  // Both sheet implementations carry `role="dialog"` (platform/Sheet.tsx and the
  // list's own), which is what makes this one query enough.
  if (document.querySelector('[role="dialog"]')) return false;
  return true;
}
